/**
 * Merge conversation avatar descriptors while protecting explicit room avatars.
 *
 * When a room already has an explicit avatar (descriptor.type === 'room'),
 * background fetches that supply only fallback member avatars should not
 * overwrite it. This prevents race conditions where stale fallback data
 * replaces a freshly uploaded custom avatar in the conversation list.
 *
 * @param {Object<string, Array>} prev - Existing descriptor map (roomId -> descriptors)
 * @param {Object<string, Array>} updates - Incoming descriptor map
 * @returns {Object<string, Array>} Merged descriptor map
 */
export const mergeAvatarDescriptors = (prev = {}, updates = {}) => {
  const result = { ...prev };

  for (const [roomId, descriptors] of Object.entries(updates)) {
    if (!Array.isArray(descriptors)) {
      continue;
    }

    const previousDescriptors = Array.isArray(prev[roomId]) ? prev[roomId] : null;
    const hadExplicitAvatar = previousDescriptors?.some(descriptor => descriptor?.type === 'room');
    const incomingHasExplicit = descriptors.some(descriptor => descriptor?.type === 'room');

    // Protect explicit avatars from being replaced by fallback-only payloads.
    if (hadExplicitAvatar && !incomingHasExplicit && descriptors.length > 0) {
      continue;
    }

    result[roomId] = descriptors;
  }

  return result;
};

export default mergeAvatarDescriptors;
